import './chart-card.scss';
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from '@mui/material';
import {useContext} from 'react';
import {AppContext} from '../../../contexts/app/context';
import {AppContextType} from '../../../contexts/app/types';
import {ThemeMode} from '../../../enums/theme-mode';
import {TimeLapse} from '../time-lapse/time-lapse';
import {TimeLapseValue} from '../../../interfaces/time-lapse-value';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Pallete} from '../../../theme/bluebird';

/**
 * Card used around the charts for the statistics dashboards.
 */
export const ChartCard = (props: {
  children: React.ReactNode;
  title: string;
  loading: boolean;
  handleRefresh: () => void;
  timeLapse?: {
    values: TimeLapseValue;
    handleChange: (values: TimeLapseValue) => void;
  };
  subtitle?: string;
}) => {
  // Get context
  const appContext: AppContextType = useContext(AppContext);

  // Get theme
  const theme = useTheme();

  return (
    <Card
      className="chart-card-container"
      variant="outlined"
      sx={{
        border: 'none',
        boxShadow: '0px 4px 16px 0px rgba(29, 41, 57, 0.15)',
        backgroundColor:
          appContext.state.currentThemeMode === ThemeMode.Light
            ? Pallete.white.base
            : theme.palette.background.paper,
      }}
    >
      <CardContent className="chart-card-content-container">
        <Typography variant="body1" component="span" className="chart-card-content-title">
          {props.title}
        </Typography>
        {props.subtitle && (
          <Typography
            variant="subtitle2"
            component="span"
            color="text.secondary"
            className="chart-card-content-subtitle"
          >
            {props.subtitle}
          </Typography>
        )}
        {props.timeLapse && (
          <TimeLapse
            className="chart-card-content-time-lapse-container"
            values={props.timeLapse.values}
            disabled={props.loading}
            handleChange={props.timeLapse.handleChange}
          />
        )}
        {!props.loading ? (
          <Box className="chart-card-content-chart-container">{props.children}</Box>
        ) : (
          <Box className="chart-card-content-circular-progress">
            <CircularProgress color="primary" size={24} />
          </Box>
        )}
      </CardContent>
      {!props.loading && (
        <Box className="chart-card-refresh-button">
          <IconButton onClick={props.handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
      )}
    </Card>
  );
};
